import { useRef, useState } from "react"
import {
	IconArrowUpFromLineOutline18 as UploadIcon,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import { AvatarCropDialog } from "@/components/avatar-crop-dialog"
import { toast } from "@/components/toast"
import {
	LOGO_ACCEPT_STRING,
	LOGO_ACCEPT_TYPES,
	LOGO_MAX_SIZE_BYTES,
	LOGO_MAX_SIZE_MB,
} from "@/screens/settings/portal-instance-form"

const SIZES = {
	sm: "h-8 min-h-8 w-8 min-w-8",
	md: "h-12 min-h-12 w-12 min-w-12",
} as const

interface Props {
	logoUrl: string | null
	onPicked: (file: File, previewUrl: string) => void
	/** Omit to hide the clear button, for a logo that has no empty state. */
	onCleared?: () => void
	size?: keyof typeof SIZES
	label?: string
	disabled?: boolean
}

/**
 * Logo well shared by the portal list row, the new-portal page and the portal
 * detail page. The well keeps one size with and without an image so adding a
 * logo never reflows the row around it.
 */
export function PortalLogoField({
	logoUrl,
	onPicked,
	onCleared,
	size = "sm",
	label = "Upload a logo for this portal",
	disabled = false,
}: Props) {
	const inputRef = useRef<HTMLInputElement | null>(null)
	const [isBroken, setIsBroken] = useState(false)
	const [cropSource, setCropSource] = useState<{
		url: string
		fileName: string
		fileType: string
	} | null>(null)

	const showImage = !!logoUrl && !isBroken
	const iconSize = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5"

	const handlePick = (file: File | undefined) => {
		if (!file) return
		if (!LOGO_ACCEPT_TYPES.includes(file.type)) {
			toast.info({
				title: "Logos have to be PNG, JPG or WebP",
				description:
					"Export the image in one of those formats, then upload it.",
			})
			return
		}
		if (file.size > LOGO_MAX_SIZE_BYTES) {
			toast.info({
				title: `Logos can be at most ${LOGO_MAX_SIZE_MB} MB`,
				description: "Pick a smaller file.",
			})
			return
		}
		setCropSource({
			url: URL.createObjectURL(file),
			fileName: file.name,
			fileType: file.type,
		})
	}

	return (
		<div className={cn("group relative flex-none", SIZES[size])}>
			<button
				type="button"
				title={label}
				disabled={disabled}
				onClick={() => inputRef.current?.click()}
				className={cn(
					"relative cursor-default overflow-hidden rounded-md ring-1 ring-inset ring-gray-900/15 hover:bg-gray-50 dark:ring-white/15 dark:hover:bg-gray-850",
					SIZES[size],
				)}
			>
				{showImage ? (
					<>
						<img
							src={logoUrl ?? ""}
							alt={label}
							className="h-full w-full object-cover"
							onError={() => setIsBroken(true)}
						/>
						<div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100">
							<UploadIcon className={cn("flex-none text-white", iconSize)} />
						</div>
					</>
				) : (
					<div className="flex h-full w-full items-center justify-center text-secondary">
						<UploadIcon className={cn("flex-none", iconSize)} />
					</div>
				)}
			</button>

			{showImage && !disabled && onCleared ? (
				<button
					type="button"
					title="Remove the logo"
					onClick={() => {
						setIsBroken(false)
						onCleared()
						if (inputRef.current) inputRef.current.value = ""
					}}
					className="absolute -right-1.5 -top-1.5 flex h-4 w-4 cursor-default items-center justify-center rounded-full bg-white text-secondary opacity-0 ring-1 ring-black/10 hover:text-primary group-hover:opacity-100 dark:bg-gray-850 dark:ring-white/10"
				>
					<X className="h-2.5 w-2.5 flex-none" />
				</button>
			) : null}

			<input
				ref={inputRef}
				type="file"
				accept={LOGO_ACCEPT_STRING}
				className="hidden"
				onChange={(e) => {
					handlePick(e.target.files?.[0])
					e.target.value = ""
				}}
			/>

			{cropSource ? (
				<AvatarCropDialog
					isOpen
					onOpenChange={(open) => {
						if (!open) {
							URL.revokeObjectURL(cropSource.url)
							setCropSource(null)
						}
					}}
					imageSrc={cropSource.url}
					fileName={cropSource.fileName}
					fileType={cropSource.fileType}
					title="Crop the portal logo"
					description="Drag to reposition and use the slider to zoom."
					onCropComplete={(croppedFile, previewUrl) => {
						setIsBroken(false)
						onPicked(croppedFile, previewUrl)
					}}
				/>
			) : null}
		</div>
	)
}
